"use client";

import { useTranslations } from "next-intl";

import { toReviewForm } from "../../personalization.utils";

import { EMAIL_PATTERN } from "../../personalization.constants";

import type { CandidateResumeUploadStepInput } from "../../personalization.types";
import { isCandidateIdentityConflict } from "../../services/personalizationClient";
export function useCandidateResumeUpload({
  controller,
  job,
  uploadSource,
  skipFitCheck,
  onParsed,
  completeApply,
  mounted,
  selectedFile,
  hasUploadConsent,
  setSelectedFile,
  setLocalError,
  setStep,
  setReviewForm,
  onIdentityConflict,
}: CandidateResumeUploadStepInput) {
  const t = useTranslations("profile.resumePersonalization");
  const chooseFile = (file: File | null) => {
    setSelectedFile(file);
    setLocalError(null);
  };

  const upload = async () => {
    if (!hasUploadConsent) return;
    if (!selectedFile) {
      setLocalError(t("errors.chooseResume"));
      return;
    }
    setStep("parsing");
    setLocalError(null);
    try {
      const result = await (async () => {
        if (uploadSource === "jobs_page") {
          return controller.uploadResume(selectedFile);
        }
        return job
          ? controller.uploadResume(selectedFile, uploadSource, job)
          : controller.uploadResume(selectedFile, uploadSource);
      })();
      if (!mounted.current) return;
      if (result.editableProfile) {
        setReviewForm(toReviewForm(result.editableProfile));
      }
      if (onParsed) {
        await onParsed(result);
        return;
      }
      if (uploadSource === "apply_gate" && skipFitCheck) {
        // Collect-only gate (remote RPH jobs): the resume is persisted and
        // parsed server-side at upload. If the parser found the candidate's
        // email, redirect immediately; otherwise collect it via review + OTP.
        const parsedEmail = result.editableProfile?.email ?? "";
        if (EMAIL_PATTERN.test(parsedEmail)) {
          await completeApply();
          return;
        }
      }
      setStep("review");
    } catch (error) {
      if (!mounted.current) return;
      if (isCandidateIdentityConflict(error)) {
        onIdentityConflict();
        return;
      }
      setStep("upload");
    }
  };

  return { chooseFile, upload };
}
